/**
 * AI Providers Page - AI 제공자 연결
 *
 * 워크플로우에서 사용할 AI 모델 제공자(Bedrock / OpenAI / Anthropic / Google)를 연결합니다.
 * (관리자 설정의 'AI 제공자' 탭에서 분리된 독립 컴포넌트)
 */

import { useState, useEffect } from 'react';
import { Topbar, PanelTitle } from '../components/layout';
import { Badge, Button, Modal, Select, useToast } from '../components/ui';
import { adminApi, BedrockAccount, ProviderStatus } from '../api/aiProvider';
import { authApi, tenantApi } from '../api';
import { isProductAdminRole, isTenantAdminRole } from '../utils/role';
import { BedrockAccountModal } from '../components/chat/BedrockAccountModal';
import awsLogo from '../assets/icons/providers/aws.svg';
import openaiLogo from '../assets/icons/providers/openai.svg';
import anthropicLogo from '../assets/icons/providers/anthropic.svg';
import geminiLogo from '../assets/icons/providers/gemini.svg';
import '../styles/adminPage.css';

// AI 제공자 타입
interface AIProvider {
  id: string;
  name: string;
  logo: string;
  description: string;
  models: string[];
  connected: boolean;
  apiKeyMasked?: string;
  inherited?: boolean;  // 상위(테넌트)에서 상속된 설정
  scope?: string;
}

// 초기 AI 제공자 목록 (제공자 추가 시 이 배열에 항목만 추가)
const DEFAULT_AI_PROVIDERS: AIProvider[] = [
  {
    id: 'bedrock',
    name: 'Amazon Bedrock',
    logo: awsLogo,
    description: 'AWS의 기반 모델 서비스',
    models: ['Amazon', 'Anthropic', 'OpenAI'],
    connected: false, // 계정을 직접 등록해야 연결됨
  },
  {
    id: 'openai',
    name: 'OpenAI',
    logo: openaiLogo,
    description: 'OpenAI 모델',
    models: ['GPT-4', 'GPT-3.5'],
    connected: false,
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    logo: anthropicLogo,
    description: 'Claude 모델 (직접 API 연결)',
    models: ['Claude'],
    connected: false,
  },
  {
    id: 'google',
    name: 'Google AI',
    logo: geminiLogo,
    description: 'Google 모델',
    models: ['Gemini'],
    connected: false,
  },
];

// 로컬 스토리지 키
const STORAGE_KEY = 'admin_ai_providers';

export function AiProvidersPage({ embedded = false }: { embedded?: boolean }) {
  const { showToast } = useToast();
  const [providers, setProviders] = useState<AIProvider[]>([]);

  const currentUser = authApi.getUser();
  const isProductAdmin = isProductAdminRole(currentUser?.role);
  const isTenantAdmin = isTenantAdminRole(currentUser?.role);
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    if (isProductAdmin) {
      tenantApi.list().then(ts => setTenants(ts)).catch(() => {});
    }
  }, [isProductAdmin]);

  // 설정 모달 상태
  const [configModal, setConfigModal] = useState<AIProvider | null>(null);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Bedrock 멀티 어카운트
  const [bedrockAccounts, setBedrockAccounts] = useState<BedrockAccount[]>([]);
  const [showBedrockModal, setShowBedrockModal] = useState(false);

  useEffect(() => {
    loadProviders();
    loadBedrockAccounts();
  }, []);

  const loadBedrockAccounts = async () => {
    try {
      const accounts = await adminApi.listBedrockAccounts();
      setBedrockAccounts(accounts);
    } catch { /* 로드 실패 무시 */ }
  };

  const [testingAccountId, setTestingAccountId] = useState<string | null>(null);

  const handleTestBedrockAccount = async (acc: BedrockAccount) => {
    setTestingAccountId(acc.id);
    // 진행 중 토스트 → 같은 id 로 결과 교체
    const toastId = showToast('loading', '연결 테스트 중...', { title: acc.name });
    try {
      const r = await adminApi.testBedrockAccount(acc.id);
      showToast('success', r.message || '연결이 정상입니다.', { id: toastId });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || '연결 테스트 실패';
      showToast('error', '', { id: toastId, title: `${acc.name} 연결 실패`, detail: msg });
    } finally {
      setTestingAccountId(null);
    }
  };

  const handleDeleteBedrockAccount = async (acc: BedrockAccount) => {
    if (acc.inherited && !isProductAdmin && !isTenantAdmin) {
      showToast('info', '상위 스코프에서 등록한 계정은 여기서 삭제할 수 없습니다.');
      return;
    }
    if (!confirm(`"${acc.name}" 계정을 삭제하시겠습니까?`)) return;
    try {
      await adminApi.deleteBedrockAccount(acc.id, acc.scope);
      setBedrockAccounts((prev) => prev.filter((a) => a.id !== acc.id));
      showToast('success', '계정이 삭제되었습니다.');
    } catch {
      showToast('error', '삭제에 실패했습니다.');
    }
  };

  const [activatingAccountId, setActivatingAccountId] = useState<string | null>(null);

  const handleActivateBedrockAccount = async (acc: BedrockAccount) => {
    if (acc.isActive) return; // 이미 활성화된 계정
    if (acc.inherited) {
      showToast('info', '상위 스코프에서 등록한 계정은 활성화할 수 없습니다.');
      return;
    }
    setActivatingAccountId(acc.id);
    try {
      await adminApi.activateBedrockAccount(acc.id);
      // 상태 업데이트: 해당 계정만 활성화, 나머지는 비활성화
      setBedrockAccounts((prev) =>
        prev.map((a) => ({ ...a, isActive: a.id === acc.id }))
      );
      showToast('success', `"${acc.name}" 계정이 활성화되었습니다.`);
    } catch {
      showToast('error', '활성화에 실패했습니다.');
    } finally {
      setActivatingAccountId(null);
    }
  };

  const loadProviders = async () => {
    // 외부 제공자 상태를 백엔드에서 조회
    let statuses: Record<string, ProviderStatus> = {};
    try {
      statuses = await adminApi.getAllProviderStatuses();
    } catch {
      // 로그인 전이거나 네트워크 오류 시 localStorage 폴백
    }

    const stored = localStorage.getItem(STORAGE_KEY);
    let localParsed: Partial<AIProvider>[] = [];
    try { localParsed = stored ? JSON.parse(stored) : []; } catch { /* ignore */ }

    const merged = DEFAULT_AI_PROVIDERS.map((def) => {
      if (def.id === 'bedrock') return def; // Bedrock 연결 여부는 어카운트 수로 렌더 시 판단
      const remote = statuses[def.id];
      if (remote) return { ...def, connected: remote.connected, apiKeyMasked: remote.apiKeyMasked, inherited: remote.inherited, scope: remote.scope };
      // 폴백: localStorage
      const local = localParsed.find((p) => p.id === def.id);
      return local ? { ...def, connected: !!local.connected, apiKeyMasked: local.apiKeyMasked } : def;
    });
    setProviders(merged);
  };

  const saveProviders = (updated: AIProvider[]) => {
    setProviders(updated);
    // localStorage에는 마스킹 상태만 유지 (실제 키는 백엔드에 저장됨)
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(updated.map(({ id, connected, apiKeyMasked }) => ({ id, connected, apiKeyMasked }))),
    );
  };

  const openConfigModal = (provider: AIProvider) => {
    setConfigModal(provider);
    setApiKeyInput('');
  };

  const closeConfigModal = () => {
    setConfigModal(null);
    setApiKeyInput('');
  };

  const handleSaveApiKey = async () => {
    if (!configModal) return;

    // Bedrock은 AWS 프로필 사용이므로 API Key 저장 불필요
    if (configModal.id === 'bedrock') {
      showToast('info', 'Amazon Bedrock은 AWS 프로필을 통해 인증됩니다.');
      closeConfigModal();
      return;
    }

    if (!apiKeyInput.trim()) {
      // 이미 연결된 제공자는 키를 바꾸지 않으면 그대로 닫음
      if (configModal.connected) {
        closeConfigModal();
        return;
      }
      showToast('error', 'API Key를 입력해주세요.');
      return;
    }

    setSaving(true);
    try {
      // 백엔드에 실제 API Key 저장
      const result = await adminApi.saveApiKey(configModal.id, apiKeyInput.trim());
      const updated = providers.map((p) =>
        p.id === configModal.id
          ? { ...p, connected: true, apiKeyMasked: result.apiKeyMasked }
          : p
      );
      saveProviders(updated);
      showToast('success', `${configModal.name} 연결이 완료되었습니다.`);
      closeConfigModal();
    } catch (error) {
      showToast('error', '설정 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async (provider: AIProvider) => {
    if (provider.id === 'bedrock') {
      showToast('info', 'Amazon Bedrock은 기본 연결이며 해제할 수 없습니다.');
      return;
    }
    try {
      await adminApi.deleteApiKey(provider.id);
    } catch { /* 이미 없는 경우 무시 */ }
    const updated = providers.map((p) =>
      p.id === provider.id ? { ...p, connected: false, apiKeyMasked: undefined } : p
    );
    saveProviders(updated);
    showToast('success', `${provider.name} 연결이 해제되었습니다.`);
    closeConfigModal();
  };

  const [testingProviderId, setTestingProviderId] = useState<string | null>(null);

  const handleTestConnection = async (provider: AIProvider) => {
    if (testingProviderId) return; // 이미 테스트 중이면 무시
    setTestingProviderId(provider.id);
    // 진행 중 토스트 → 같은 id 로 결과 교체
    const toastId = showToast('loading', '연결 테스트 중...', { title: provider.name });
    try {
      const result = await adminApi.testConnection(provider.id);
      showToast('success', result.message || `${provider.name} 연결이 정상입니다.`, { id: toastId });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        || (err as Error)?.message
        || '연결 테스트 실패';
      showToast('error', '', { id: toastId, title: `${provider.name} 연결 실패`, detail: msg });
    } finally {
      setTestingProviderId(null);
    }
  };

  // Bedrock은 등록된 어카운트 수로 연결 여부 판단
  const filteredBedrockAccounts = tenantFilter
    ? bedrockAccounts.filter(acc => acc.scope === `tenant:${tenantFilter}`)
    : bedrockAccounts;
  const bedrockConnected = filteredBedrockAccounts.length > 0;
  const isConnectedGlobal = (p: AIProvider) => (p.id === 'bedrock' ? bedrockConnected : p.connected);
  const connectedCount = providers.filter(isConnectedGlobal).length;

  // 카드 렌더 헬퍼 (tenantAccts: 해당 테넌트의 Bedrock 계정 목록)
  const renderProviderCard = (provider: AIProvider, tenantAccts: BedrockAccount[]) => {
    const tenantBedrockConn = tenantAccts.length > 0;
    const connected = provider.id === 'bedrock' ? tenantBedrockConn : provider.connected;

    return (
      <div key={provider.id} className={`provider-card${connected ? ' connected' : ''}`}>
        <div className="provider-card-top">
          <div className="provider-logo"><img src={provider.logo} alt="" /></div>
          {connected ? (
            provider.inherited ? <Badge color="info">상위 상속</Badge> : <Badge color="success">연결됨</Badge>
          ) : (
            <Badge color="gray">미연결</Badge>
          )}
        </div>

        <div>
          <div className="provider-name">{provider.name}</div>
          <div className="provider-desc">{provider.description}</div>
        </div>

        <div className="choice-card-tags provider-models">
          {provider.models.map((model) => (
            <Badge key={model} variant="type">{model}</Badge>
          ))}
        </div>

        {provider.connected && provider.inherited ? (
          <div className="provider-key">
            <span style={{ color: 'var(--text-3)', fontSize: 12 }}>
              상위 테넌트에서 설정한 연결을 사용 중입니다. 직접 연결하면 이 설정으로 덮어씁니다.
            </span>
          </div>
        ) : provider.connected && provider.apiKeyMasked ? (
          <div className="provider-key">
            API Key <code>{provider.apiKeyMasked}</code>
          </div>
        ) : null}

        {provider.id === 'bedrock' ? (
          tenantBedrockConn ? (
            <>
              <div className="bedrock-account-list">
                {tenantAccts.map((acc) => (
                  <div key={acc.id} className={`bedrock-account-row${acc.isActive ? ' active' : ''}`}>
                    <label className="bedrock-account-radio" title={acc.inherited ? '상위 스코프 계정은 활성화할 수 없습니다' : '이 계정 활성화'}>
                      <input
                        type="radio"
                        name="bedrock-active-account"
                        checked={acc.isActive || false}
                        onChange={() => handleActivateBedrockAccount(acc)}
                        disabled={acc.inherited || activatingAccountId !== null}
                      />
                      <span className="bedrock-account-info">
                        <strong>{acc.name}</strong>
                        <span>{acc.authType === 'assumeRole' ? 'Assume Role' : 'Access Key'} · {acc.region}</span>
                      </span>
                      <span className="bedrock-account-actions">
                        <button className="bedrock-account-btn bedrock-account-btn-test"
                          onClick={() => handleTestBedrockAccount(acc)}
                          disabled={testingAccountId === acc.id} title="연결 테스트">
                          {testingAccountId === acc.id ? '…' : '테스트'}
                        </button>
                        {(acc.inherited && !isProductAdmin && !isTenantAdmin) ? (
                          <span style={{ fontSize: 11, color: 'var(--text-3)', padding: '0 6px' }}>상위에서 관리</span>
                        ) : (
                          <button className="bedrock-account-btn bedrock-account-btn-del"
                            onClick={() => handleDeleteBedrockAccount(acc)} title="삭제">계정 삭제</button>
                        )}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
              <div className="provider-actions">
                <Button color="gray" appearance="outline" onClick={() => setShowBedrockModal(true)}>
                  + 계정 추가
                </Button>
              </div>
            </>
          ) : (
            <div className="provider-actions">
              <Button color="primary" onClick={() => setShowBedrockModal(true)}>연결하기</Button>
            </div>
          )
        ) : (
          <div className="provider-actions">
            {provider.connected && !provider.inherited ? (
              <>
                <Button color="gray" appearance="outline"
                  onClick={() => handleTestConnection(provider)}
                  disabled={testingProviderId === provider.id}>
                  {testingProviderId === provider.id ? '테스트 중...' : '테스트'}
                </Button>
                <Button color="error" appearance="outline" onClick={() => handleDisconnect(provider)}>
                  연결해제
                </Button>
              </>
            ) : (
              <Button color="primary" onClick={() => openConfigModal(provider)}>
                {provider.inherited ? '직접 연결' : '연결하기'}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {!embedded && (
        <Topbar title="AI 제공자 연결" description="워크플로우에서 사용할 AI 모델 제공자를 연결합니다" />
      )}

      <div className={embedded ? '' : 'page-content'}>
        <section>
          <div className={`admin-panel-head${embedded ? '' : ' admin-panel-head--compact'}`}>
            {embedded ? (
              <PanelTitle
                title="AI 제공자 연결"
                description="워크플로우에서 사용할 AI 모델 제공자를 연결합니다. 연결된 제공자의 모델만 워크플로우 AI 응답 노드에서 선택할 수 있습니다."
              />
            ) : <span />}
            <div className="admin-panel-actions">
              {isProductAdmin && tenants.length > 0 && (
                <Select value={tenantFilter} onChange={e => setTenantFilter(e.target.value)}>
                  <option value="">전체 테넌트</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </Select>
              )}
              <div className="provider-count">
                <b>{connectedCount}</b> / {providers.length} 연결됨
              </div>
            </div>
          </div>

          {/* 전체 테넌트 + productAdmin: 테넌트별 그룹 뷰 */}
          {!tenantFilter && isProductAdmin && tenants.length > 0 ? (
            <div className="provider-tenant-sections">
              {tenants.map(tenant => {
                const tenantAccts = bedrockAccounts.filter(acc => acc.scope === `tenant:${tenant.id}`);
                return (
                  <div key={tenant.id} className="provider-tenant-section">
                    <div className="provider-tenant-label">{tenant.name}</div>
                    <div className="provider-grid">
                      {providers.map(provider => renderProviderCard(provider, tenantAccts))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* 특정 테넌트 or 일반 관리자: 기존 4-카드 그리드 */
            <div className="provider-grid">
              {providers.map(provider => renderProviderCard(provider, filteredBedrockAccounts))}
            </div>
          )}
        </section>
      </div>

      {/* 제공자 연결 / 설정 모달 */}
      <Modal
        isOpen={!!configModal}
        onClose={closeConfigModal}
        title={configModal ? `${configModal.name} ${configModal.connected ? '설정' : '연결'}` : ''}
        size="sm"
        footerLeft={
          configModal?.connected && configModal.id !== 'bedrock' ? (
            <Button color="error" appearance="outline" onClick={() => handleDisconnect(configModal)}>
              연결 해제
            </Button>
          ) : undefined
        }
        footerRight={
          <>
            <Button color="gray" appearance="outline" onClick={closeConfigModal}>
              {configModal?.id === 'bedrock' ? '닫기' : '취소'}
            </Button>
            {configModal && configModal.id !== 'bedrock' && (
              <Button color="primary" onClick={handleSaveApiKey} disabled={saving}>
                {saving ? '저장 중...' : configModal.connected ? '저장' : '연결'}
              </Button>
            )}
          </>
        }
      >
        {configModal?.id === 'bedrock' ? (
          <div className="config-info">
            <p>Amazon Bedrock은 AWS 프로필을 통해 인증됩니다.</p>
            <p>현재 프로필: <strong>lab-on</strong></p>
            <p className="config-hint">
              다른 프로필을 사용하려면 백엔드 환경 변수를 수정하세요.
            </p>
          </div>
        ) : configModal && (
          <>
            <label className="provider-key-label" htmlFor="provider-api-key">API Key</label>
            <div className="input">
              <input
                id="provider-api-key"
                type="password"
                className="input-control"
                placeholder={
                  configModal.connected
                    ? `현재 ${configModal.apiKeyMasked ?? ''} · 변경할 때만 입력`
                    : `${configModal.name} API Key를 입력하세요`
                }
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                autoFocus
              />
            </div>
            <p className="config-hint">
              API Key는 암호화되어 안전하게 저장됩니다.
              {configModal.id === 'openai' && (
                <> <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">OpenAI API Keys</a>에서 발급받을 수 있습니다.</>
              )}
              {configModal.id === 'anthropic' && (
                <> <a href="https://console.anthropic.com/account/keys" target="_blank" rel="noreferrer">Anthropic Console</a>에서 발급받을 수 있습니다.</>
              )}
              {configModal.id === 'google' && (
                <> <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer">Google AI Studio</a>에서 발급받을 수 있습니다.</>
              )}
            </p>
          </>
        )}
      </Modal>

      {/* Bedrock 계정 등록 마법사 */}
      <BedrockAccountModal
        isOpen={showBedrockModal}
        onClose={() => setShowBedrockModal(false)}
        onSaved={loadBedrockAccounts}
      />
    </>
  );
}

export default AiProvidersPage;
